import { ALL_KINDS, type ImportKind, type ImportMode } from "../../utils/bpaImport";
import { ChipCheckbox } from "./controls/ChipCheckbox";
import { SegmentedToggle, type SegmentedOption } from "./controls/SegmentedToggle";
import { KIND_LABEL, MOVE_NEEDS_DELETE_REASON } from "./importText";
import { ImportSection } from "./ImportSection";
import styles from "./ContentSection.module.css";

type ContentSectionProps = {
  open: boolean;
  focusToken: number;
  kinds: ImportKind[];
  mode: ImportMode;
  moveAllowed: boolean;
  moveLockedReason?: string | null;
  notice?: string | null;
  split?: { line: string } | null;
  scoresComeAcross?: boolean;
  onToggleKind: (kind: ImportKind) => void;
  onModeChange: (mode: ImportMode) => void;
};

const COPY: SegmentedOption<ImportMode> = { value: "copy", text: "Copy" };
const MOVE: SegmentedOption<ImportMode> = { value: "move", text: "Move" };
const MOVE_DISABLED: SegmentedOption<ImportMode> = { ...MOVE, disabled: true };

const SCORES_NOTE = " SARs scores come with their submissions.";

function modeHint(mode: ImportMode, scoresComeAcross: boolean): string {
  const scores = scoresComeAcross ? SCORES_NOTE : "";
  if (mode === "move") {
    return `Move: content that is imported is removed from the source, and content you leave out stays there.${scores}`;
  }
  return `Copy: the source keeps its own content.${scores}`;
}

export function ContentSection({
  open,
  focusToken,
  kinds,
  mode,
  moveAllowed,
  moveLockedReason = null,
  notice = null,
  split = null,
  scoresComeAcross = true,
  onToggleKind,
  onModeChange,
}: ContentSectionProps) {
  return (
    <ImportSection
      number={2}
      title="What to bring across"
      description={open ? undefined : "Choose a practice area first."}
      status={open ? "open" : "locked"}
      valid={open && kinds.length > 0}
      focusToken={focusToken}
    >
      <div className={styles.row}>
        <div role="group" aria-label="Content to bring across" className={styles.chips}>
          {ALL_KINDS.map(kind => (
            <ChipCheckbox
              key={kind}
              label={KIND_LABEL[kind]}
              checked={kinds.includes(kind)}
              disabled={split !== null}
              onChange={() => onToggleKind(kind)}
            />
          ))}
        </div>
        {!split && (
          <SegmentedToggle
            className={styles.mode}
            name="import-mode"
            ariaLabel="Copy or move"
            value={mode}
            options={[COPY, moveAllowed && !moveLockedReason ? MOVE : MOVE_DISABLED]}
            onChange={onModeChange}
          />
        )}
      </div>
      <p className={styles.hint}>
        {split ? split.line : modeHint(mode, scoresComeAcross && kinds.includes("submissions"))}
      </p>
      {moveLockedReason && <p className={styles.hint}>{moveLockedReason}</p>}
      {moveAllowed ? null : <p className={styles.hint}>{MOVE_NEEDS_DELETE_REASON}</p>}
      {notice && (
        <p className={styles.hint} role="status">
          {notice}
        </p>
      )}
    </ImportSection>
  );
}
